import { _dataProvider } from '../dataProvider/DataProvider';
import BooksInBible from '../dataProvider/AppBible/bible.json';
import { SERVER_URL } from '../SharedCommon/utils';
import { getJwtObj } from '../SharedCommon/utils';
import defaultAvatar from '../img/icon-DefaultProfilePicture.png';
import chatVoiceInputIcon from '../img/icon-chatVoice-input.png';

export interface IRawMessage {
  message?: string;
  text?: string;
  messageId?: string | number;
  id?: string | number;
  time?: number;
  createdAt?: Date | string | number;
  type?: number;
  quote?: string | null;
  attachmentUrl?: string;
  attachment?: string | object | any[];
  imageUrl?: string;
  thumbnailUrl?: string;
  audioUrl?: string;
  user?: {
    id: number;
    displayName?: string;
    loginId?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

export interface IGiftedMessage {
  id: string | number;
  createdAt: Date;
  message: string;
  time: number;
  user: {
    id: number;
    name: string;
    displayName: string;
    loginId: string;
    avatar: string;
    defaultAvatar: string;
  };
  quote?: string | null;
  text?: string;
  image?: string;
  thumbnail?: string;
  audio?: {
    url: string;
    duration: number;
  };
  attachment?: {
    filename: string;
    fileSize: number;
    url: string;
  };
  card?: string[];
  groupTaskCard?: {
    kind: 'checkin' | 'checkinSuccess' | 'signup' | 'readingPlan';
    title: string;
    id?: string;
    description?: string;
    deadline?: number | string | null;
    scriptures?: string[];
    attendance?: Array<{ name: string; userId?: number }>;
    progress?: number;
    timeRemaining?: number | null;
    image?: string;
  };
  preCompletedNotice?: {
    title?: string;
    checkInId: string;
    users: Array<{ userId: string; displayName: string | null }>;
  };
  type?: string;
  mergedForwardTitle?: string;
  mergedForwardContent?: string;
  mergedForwardAttachmentUrl?: string;
  mergedForwardAttachment?: string | object | any[];
  mergedForwardMessages?: any[];
  textStyle?: {
    textDecoration?: string;
    textDecorationColor?: string;
  };
  icon?: {
    image: string;
    style: {
      width: string;
      height: string;
      verticalAlign: string;
      filter: string;
    };
  };
  [key: string]: any;
}

export interface IParseMessageOptions {
  groupId?: number;
  supportBase64?: boolean;
  currentUserId?: number;
  // When the group hides its member list, the sender's uniqueId must not be exposed
  // in the inline author name, except for group leaders (whose ids may still be shown).
  disableMemberShowList?: boolean;
  leaderUserIds?: number[];
  currentUserIsGroupLeader?: boolean;
}

const getBlobUrl = (url: string): string => {
  if (!url || typeof url !== 'string') {
    return '';
  }
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;

    if (pathname.includes('..') || pathname.includes('%2e%2e') || pathname.includes('%2E%2E')) {
      return '';
    }

    return `${SERVER_URL}${pathname}${urlObj.search}`;
  } catch (_error) {
    return '';
  }
};

/**
 * 检查编码后的字符串是否包含危险字符的编码形式
 * 用于在 decodeURIComponent 之前进行安全检查
 */
const containsDangerousEncodedChars = (encodedStr: string): boolean => {
  if (!encodedStr || typeof encodedStr !== 'string') {
    return false;
  }
  // 检查常见的危险字符编码：.. / \ 等
  const dangerousPatterns = [
    /%2e%2e/i, // ..
    /%2f/i, // /
    /%5c/i, // \
    /\.\./, // 未编码的 ..
    /\//, // 未编码的 /
    /\\/, // 未编码的 \
    /%00/i, // null byte
    /%0a/i, // newline
    /%0d/i // carriage return
  ];
  return dangerousPatterns.some((pattern) => pattern.test(encodedStr));
};

/**
 * 安全地解码文件名，先检查编码后的字符串，再 decodeURIComponent
 */
const safeDecodeFilename = (encodedFilename: string): string => {
  if (!encodedFilename || typeof encodedFilename !== 'string') {
    return 'file';
  }
  // 先检查编码后的字符串
  if (containsDangerousEncodedChars(encodedFilename)) {
    return 'file';
  }
  try {
    const decoded = decodeURIComponent(encodedFilename);
    // 再次检查解码后的字符串（双重检查）
    if (decoded.includes('..') || decoded.includes('/') || decoded.includes('\\')) {
      return 'file';
    }
    return decoded;
  } catch (_error) {
    return 'file';
  }
};

const getMimeTypeFromFilename = (filename: string): string => {
  const lowerFilename = filename.toLowerCase();
  if (lowerFilename.endsWith('.pdf')) {
    return 'application/pdf';
  } else if (lowerFilename.endsWith('.doc') || lowerFilename.endsWith('.docx')) {
    return 'application/msword';
  } else if (lowerFilename.endsWith('.xls') || lowerFilename.endsWith('.xlsx')) {
    return 'application/vnd.ms-excel';
  } else if (lowerFilename.endsWith('.zip') || lowerFilename.endsWith('.rar')) {
    return 'application/zip';
  } else if (lowerFilename.endsWith('.mp4') || lowerFilename.endsWith('.mov')) {
    return 'video/mp4';
  } else if (lowerFilename.endsWith('.mp3')) {
    return 'audio/mpeg';
  } else if (lowerFilename.endsWith('.jpg') || lowerFilename.endsWith('.jpeg')) {
    return 'image/jpeg';
  } else if (lowerFilename.endsWith('.png')) {
    return 'image/png';
  }
  return 'application/octet-stream';
};

const getAudioMimeType = (filename: string): string => {
  if (filename.endsWith('.mp3') || filename.includes('.mp3')) {
    return 'audio/mpeg';
  } else if (filename.endsWith('.wav') || filename.includes('.wav')) {
    return 'audio/wav';
  } else if (filename.endsWith('.webm') || filename.includes('.webm')) {
    return 'audio/webm';
  }
  return 'audio/webm';
};

const createSafeDataUri = (base64String: string, mimeType: string): string => {
  const MAX_BASE64_LENGTH = 50000000;
  const BASE64_REGEX = /^[A-Za-z0-9+/=]*$/;

  if (!BASE64_REGEX.test(base64String)) {
    console.warn('Invalid base64 format detected, skipping Data URI creation');
    return '';
  }

  if (base64String.length > MAX_BASE64_LENGTH) {
    console.warn(
      `Base64 string too large (${base64String.length} chars, max ${MAX_BASE64_LENGTH}), skipping Data URI creation`
    );
    return '';
  }

  return `data:${mimeType};base64,${base64String}`;
};

const CHECKIN_PREFIX = '!checkin!';
const CHECKIN_SUCCESS_PREFIX = '!checkin_record!';
const READING_PLAN_CARD_TYPE = '!card!';

const parseObjectData = (value: unknown): Record<string, any> | null => {
  if (!value) {
    return null;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, any>;
  }
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const normalizeString = (value: unknown): string => (typeof value === 'string' ? value : '');

const normalizePositiveNumber = (value: unknown): number => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : 0;
};

const normalizeNonNegativeInteger = (value: unknown): number | null => {
  if (typeof value !== 'number' && (typeof value !== 'string' || !value.trim())) {
    return null;
  }
  const numberValue = Number(value);
  return Number.isSafeInteger(numberValue) && numberValue >= 0 ? numberValue : null;
};

const normalizeUserId = (value: unknown): string | null => {
  const id = normalizeNonNegativeInteger(value);
  return id !== null && id > 0 ? String(id) : null;
};

const BIBLE_BOOK_NAMES = (BooksInBible as string[]).map((book) => book.split('|')[0] || '');
const VERSE_ID_MAX = 99 * 1000000 + 999 * 1000 + 999;

const parseVerseId = (value: unknown): { bookId: number; chapter: number; verse: number } | null => {
  const verseId = Number(value);
  if (!Number.isFinite(verseId) || verseId < 0 || verseId > VERSE_ID_MAX) {
    return null;
  }

  const id = Math.floor(verseId);
  const bookId = Math.floor(id / 1000000);
  const chapter = Math.floor((id % 1000000) / 1000);
  const verse = id % 1000;

  if (!BIBLE_BOOK_NAMES[bookId - 1] || chapter <= 0 || verse <= 0) {
    return null;
  }

  return { bookId, chapter, verse };
};

const formatVerseRange = (startId: unknown, endId: unknown): string | null => {
  const start = parseVerseId(startId);
  const end = parseVerseId(endId);
  if (!start || !end) {
    return null;
  }

  const startBookName = BIBLE_BOOK_NAMES[start.bookId - 1];
  const endBookName = BIBLE_BOOK_NAMES[end.bookId - 1];

  if (start.bookId === end.bookId && start.chapter === end.chapter) {
    return start.verse === end.verse
      ? `${startBookName}${start.chapter}:${start.verse}`
      : `${startBookName}${start.chapter}:${start.verse}-${end.verse}`;
  }

  return `${startBookName}${start.chapter}:${start.verse}-${endBookName}${end.chapter}:${end.verse}`;
};

const formatScriptureReference = (value: unknown): string => {
  const raw = String(value);
  const rangeMatch = raw.match(/^\s*(\d{7,9})\s*-\s*(\d{7,9})\s*$/);
  if (rangeMatch) {
    return formatVerseRange(rangeMatch[1], rangeMatch[2]) || raw;
  }

  const singleMatch = raw.match(/^\s*(\d{7,9})\s*$/);
  if (singleMatch) {
    return formatVerseRange(singleMatch[1], singleMatch[1]) || raw;
  }

  return raw;
};

const normalizeScriptures = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(formatScriptureReference).filter(Boolean) : [];

const parseReadingPlanCardMessage = (text: string) => {
  const trimmed = text.trim();
  const marker = 'readingPlanCard=1';
  const urlRegex = /https?:\/\/[^\s]*#\/lesson\/(\d+)(?:\/\d+)?(?:\?[^\s]*)?/i;
  const lines = trimmed
    .replace(/\\n/g, '\n')
    .split(/\r?\n/)
    .map((line) => line.trim());

  if (lines.length >= 3 && lines[lines.length - 1]?.includes(marker)) {
    const match = lines[lines.length - 1].match(urlRegex);
    const lessonId = normalizePositiveNumber(match?.[1]);
    if (lessonId) {
      return {
        lessonId,
        lessonName: normalizeString(lines[0]),
        lessonDescription: lines.slice(1, -1).join('\n'),
        verses: []
      };
    }
  }

  const rawJson = trimmed.startsWith(READING_PLAN_CARD_TYPE)
    ? trimmed.slice(READING_PLAN_CARD_TYPE.length).trim()
    : trimmed;
  const data = parseObjectData(rawJson);
  if (!data || data.type !== READING_PLAN_CARD_TYPE) {
    return null;
  }

  const lessonId = normalizePositiveNumber(data.lessonId);
  if (!lessonId) {
    return null;
  }

  return {
    lessonId,
    lessonName: normalizeString(data.lessonName || data.title),
    lessonDescription: normalizeString(data.lessonDescription || data.desc),
    verses: data.verses
  };
};

const getMessageObjectData = (msg: IRawMessage): Record<string, any> | null =>
  parseObjectData(msg.data) || parseObjectData(msg.attachment);

const buildPreCompletedNotice = (msg: IRawMessage, text: string, options: IParseMessageOptions) => {
  if (msg.system || (msg.type !== 6 && !text.startsWith(CHECKIN_PREFIX))) {
    return null;
  }

  // Structured message types take precedence over legacy text markers, matching iDigestApp.
  if (msg.type === 7 || msg.type === 8 || msg.type === 9) {
    return null;
  }

  const data = getMessageObjectData(msg);
  if (!data || data.kind === 'readingPlanStart') {
    return null;
  }

  const checkInId = normalizeUserId(data.id);
  if (!checkInId || !Array.isArray(data.preCompletedUsers)) {
    return null;
  }

  const currentUserId = normalizeUserId(options.currentUserId);
  const leaderIds = new Set((options.leaderUserIds || []).map(normalizeUserId).filter(Boolean));
  const seen = new Set<string>();
  const users: Array<{ userId: string; displayName: string | null }> = [];

  data.preCompletedUsers.forEach((user: unknown) => {
    if (!user || typeof user !== 'object' || Array.isArray(user)) {
      return;
    }
    const userRecord = user as Record<string, unknown>;
    const userId = normalizeUserId(userRecord.userId);
    if (!userId || seen.has(userId)) {
      return;
    }
    seen.add(userId);
    const hideName =
      !!options.disableMemberShowList &&
      !options.currentUserIsGroupLeader &&
      userId !== currentUserId &&
      !leaderIds.has(userId);
    const rawName = typeof userRecord.displayName === 'string' ? userRecord.displayName.trim() : '';
    users.push({ userId, displayName: hideName ? null : rawName || null });
  });

  if (users.length === 0) {
    return null;
  }

  const className = normalizeString(data.className).trim();
  const description = normalizeString(data.description).trim();
  const unitIndex = normalizeNonNegativeInteger(data.planUnitIndex ?? data.unitIndex);
  const title = className && unitIndex !== null ? `${className} #${unitIndex + 1}` : description || undefined;
  return { title, checkInId, users };
};

const getDeadlineFromCheckInData = (data: Record<string, any>): number | null => {
  const absoluteDeadline = normalizePositiveNumber(data.deadline);
  if (absoluteDeadline) {
    return absoluteDeadline * 1000;
  }

  const timeRemaining = normalizePositiveNumber(data.timeRemaining);
  return timeRemaining ? Date.now() + Math.min(timeRemaining, 365 * 24 * 60 * 60) * 1000 : null;
};

const buildGroupTaskCard = (msg: IRawMessage, text: string) => {
  const messageType = msg.type;
  const data = getMessageObjectData(msg);
  const readingPlanCard = parseReadingPlanCardMessage(text);

  if (messageType === 6 || text.startsWith(CHECKIN_PREFIX)) {
    const isReadingPlanStart = data?.kind === 'readingPlanStart';
    return {
      kind: isReadingPlanStart ? ('readingPlan' as const) : ('checkin' as const),
      title: isReadingPlanStart ? '读经计划学习通知' : '打卡',
      id: data?.id != null ? String(data.id) : '',
      description: normalizeString(data?.description || data?.className),
      deadline: data ? getDeadlineFromCheckInData(data) : null,
      scriptures: normalizeScriptures(readingPlanCard?.verses || data?.verses),
      image: data?.classId ? _dataProvider.getBookCover(data.classId) : undefined
    };
  }

  if (messageType === 7 || text.startsWith(CHECKIN_SUCCESS_PREFIX)) {
    return {
      kind: 'checkinSuccess' as const,
      title: '打卡',
      id: data?.id != null ? String(data.id) : '',
      description: normalizeString(data?.completeMessage || data?.description),
      progress: typeof data?.progress === 'number' ? data.progress : 0,
      timeRemaining: typeof data?.timeRemaining === 'number' ? data.timeRemaining : null,
      scriptures: normalizeScriptures(data?.verses)
    };
  }

  if (messageType === 8 || text.startsWith('!signup!')) {
    return {
      kind: 'signup' as const,
      title: '接龙',
      id: data?.id != null ? String(data.id) : '',
      description: normalizeString(data?.description),
      attendance: Array.isArray(data?.attendance) ? data.attendance : []
    };
  }

  if (messageType === 9 || readingPlanCard) {
    return {
      kind: 'readingPlan' as const,
      title: readingPlanCard?.lessonName || normalizeString(data?.title) || '读经计划学习通知',
      description: readingPlanCard?.lessonDescription || normalizeString(data?.desc),
      scriptures: normalizeScriptures(readingPlanCard?.verses || data?.verses),
      image: readingPlanCard?.lessonId
        ? _dataProvider.getBookCover(readingPlanCard.lessonId)
        : normalizeString(data?.image)
    };
  }

  return null;
};

const parseFileUrl = (
  text: string,
  messageId: number | string,
  msg: IRawMessage,
  options: {
    groupId?: number;
    supportBase64?: boolean;
  }
): string | null => {
  const urlMatch = text.match(/https?:\/\/[^\s<>"']+/);
  if (urlMatch && urlMatch[0]) {
    try {
      new URL(urlMatch[0]);
      return getBlobUrl(urlMatch[0]);
    } catch (_e) {
      // Ignore URL parsing errors
    }
  }

  if (options.groupId) {
    const numericMessageId = typeof messageId === 'string' ? parseInt(messageId, 10) : messageId;
    if (!isNaN(numericMessageId) && numericMessageId > 0) {
      return _dataProvider.getFileUrlInChat(options.groupId, numericMessageId);
    }
  }

  if (options.supportBase64) {
    if (msg.attachmentUrl) {
      return getBlobUrl(msg.attachmentUrl);
    } else if (msg.attachment) {
      const attachment = msg.attachment;
      if (typeof attachment === 'string') {
        if (attachment.startsWith('data:')) {
          return attachment;
        } else if (attachment.length > 100) {
          const pa = text.split('!');
          const filename = pa[2] ? safeDecodeFilename(pa[2]) : 'file';
          const mimeType = getMimeTypeFromFilename(filename);
          const dataUri = createSafeDataUri(attachment, mimeType);
          return dataUri || null;
        }
      }
    }
  }

  return null;
};

const parseImageUrl = (
  text: string,
  pa: string[],
  msg: IRawMessage,
  options: {
    supportBase64?: boolean;
  }
): string | null => {
  if (pa[2]) {
    if (pa[2].startsWith('http://') || pa[2].startsWith('https://')) {
      return getBlobUrl(pa[2]);
    }
  }

  const urlMatch = text.match(/https?:\/\/[^\s<>"']+/);
  if (urlMatch && urlMatch[0]) {
    return getBlobUrl(urlMatch[0]);
  }

  if (options.supportBase64) {
    if (msg.attachmentUrl) {
      return getBlobUrl(msg.attachmentUrl);
    } else if (msg.imageUrl) {
      return getBlobUrl(msg.imageUrl);
    } else if (msg.attachment) {
      const attachment = msg.attachment;
      if (typeof attachment === 'string') {
        if (attachment.startsWith('data:image/')) {
          return attachment;
        } else if (attachment.startsWith('/9j/') || attachment.startsWith('iVBORw0KGgo')) {
          const mimeType = attachment.startsWith('/9j/') ? 'image/jpeg' : 'image/png';
          const dataUri = createSafeDataUri(attachment, mimeType);
          return dataUri || null;
        }
      }
    }
  }

  return null;
};

const parseAudioUrl = (
  text: string,
  pa: string[],
  msg: IRawMessage,
  options: {
    groupId?: number;
    supportBase64?: boolean;
  }
): string | null => {
  const urlMatch = text.match(/https?:\/\/[^\s<>"']+/);
  if (urlMatch && urlMatch[0]) {
    try {
      new URL(urlMatch[0]);
      return getBlobUrl(urlMatch[0]);
    } catch (_e) {
      // Ignore URL parsing errors
    }
  }

  if (options.groupId) {
    const msgId = msg.messageId || msg.id;
    if (msgId !== undefined) {
      const numericMessageId = typeof msgId === 'string' ? parseInt(msgId, 10) : msgId;
      if (typeof numericMessageId === 'number' && !isNaN(numericMessageId) && numericMessageId > 0) {
        return _dataProvider.getFileUrlInChat(options.groupId, numericMessageId);
      }
    }
  }

  if (options.supportBase64) {
    if (msg.attachmentUrl) {
      return getBlobUrl(msg.attachmentUrl);
    } else if (msg.audioUrl) {
      return getBlobUrl(msg.audioUrl);
    } else if (msg.attachment) {
      const attachment = msg.attachment;
      if (typeof attachment === 'string') {
        if (attachment.startsWith('data:audio/')) {
          return attachment;
        } else if (attachment.startsWith('GkXfo') || attachment.length > 100) {
          const filename = pa[3] || '';
          const mimeType = getAudioMimeType(filename);
          const dataUri = createSafeDataUri(attachment, mimeType);
          return dataUri || null;
        }
      }
    }
  }

  return null;
};

export const parseMessageToGiftedFormat = (
  msg: IRawMessage,
  index: number,
  options: IParseMessageOptions = {}
): IGiftedMessage | null => {
  const text = (msg.message || msg.text || '') as string;
  const user = msg.user;

  if (!user || typeof user !== 'object') {
    return null;
  }

  const userId = user.id;
  if (!userId || typeof userId !== 'number') {
    return null;
  }

  // Use messageId from backend response - this should be the NEW message_id in the target group for forwarded messages
  const messageId = msg.messageId || msg.id || `parsed-${index}-${Date.now()}`;

  let time: number;
  if (msg.time && typeof msg.time === 'number') {
    time = msg.time;
  } else if (msg.createdAt) {
    try {
      const createdAt =
        typeof msg.createdAt === 'string' || typeof msg.createdAt === 'number'
          ? new Date(msg.createdAt)
          : (msg.createdAt as Date);
      time = Math.floor(createdAt.getTime() / 1000);
    } catch (_error) {
      time = Math.floor(Date.now() / 1000);
    }
  } else {
    time = Math.floor(Date.now() / 1000);
  }

  if (!time || !isFinite(time)) {
    return null;
  }

  const currentUserId = options.currentUserId || getJwtObj().userId;

  const includeLoginId = !options.disableMemberShowList || (options.leaderUserIds?.includes(userId) ?? false);

  const giftedm: IGiftedMessage = {
    id: messageId,
    createdAt: new Date(time * 1000),
    message: text,
    time: time,
    user: {
      id: userId,
      name: _dataProvider.getDisplayNameForUser(user, includeLoginId),
      displayName: user.displayName || '',
      loginId: user.loginId || '',
      avatar: _dataProvider.getAvatarSync(userId),
      defaultAvatar
    },
    quote: msg.quote || null
  };

  const pa = text.split('!');
  const groupTaskCard = buildGroupTaskCard(msg, text);
  const preCompletedNotice = buildPreCompletedNotice(msg, text, options);

  if (preCompletedNotice) {
    giftedm.preCompletedNotice = preCompletedNotice;
  }

  if (groupTaskCard) {
    giftedm.groupTaskCard = groupTaskCard;
    giftedm.type = groupTaskCard.kind;
    giftedm.text = groupTaskCard.title;
  } else if (text.startsWith('!file!')) {
    if (!pa[2] || !pa[3]) {
      if (options.groupId) {
        giftedm.text = 'InvalidFile';
        return giftedm;
      }
    } else {
      // 使用安全解码函数，先检查编码后的字符串，再 decodeURIComponent
      const filename = safeDecodeFilename(pa[2]);
      const fileSize = parseInt(pa[3], 10);

      giftedm.text = `${filename} (${(fileSize / 1024).toFixed(2)}KB)`;

      const fileUrl = parseFileUrl(text, messageId, msg, options);

      if (fileUrl) {
        giftedm.attachment = {
          filename: filename,
          fileSize: fileSize,
          url: fileUrl
        };
        giftedm.textStyle = { textDecoration: 'underline' };
        if (currentUserId === userId) {
          giftedm.textStyle.textDecorationColor = 'white';
        }
      } else if (options.groupId) {
        giftedm.text = 'InvalidFile';
        return giftedm;
      }
    }
  } else if (text.startsWith('!image!')) {
    if (!pa[2]) {
      if (options.groupId) {
        giftedm.text = 'InvalidImage';
        return giftedm;
      }
    } else {
      const imageUrl = parseImageUrl(text, pa, msg, options);
      if (imageUrl) {
        giftedm.image = imageUrl;
        giftedm.thumbnail = msg.thumbnailUrl;
      } else if (options.groupId) {
        giftedm.text = 'InvalidImage';
        return giftedm;
      }
    }
  } else if (text.startsWith('!audio!')) {
    if (!pa[2]) {
      giftedm.text = 'InvalidAudio';
      return giftedm;
    }

    const duration = parseInt(pa[2], 10);
    if (!Number.isFinite(duration) || duration <= 0 || duration > 600000) {
      giftedm.text = 'InvalidAudio';
      return giftedm;
    }

    giftedm.text = `${(duration / 1000).toFixed(1)}"`;

    const audioUrl = parseAudioUrl(text, pa, msg, options);

    if (!audioUrl) {
      giftedm.text = 'AudioUrlMissing';
      return giftedm;
    }

    giftedm.audio = {
      url: audioUrl,
      duration: duration
    };

    const isCurrentUser = currentUserId === userId;
    giftedm.icon = {
      image: chatVoiceInputIcon,
      style: {
        width: '20px',
        height: '20px',
        verticalAlign: 'middle',
        filter: isCurrentUser ? 'brightness(0) invert(1)' : 'brightness(0)'
      }
    };
  } else if (text.startsWith('!contact!')) {
    giftedm.card = pa.slice(2);
  } else if (text.startsWith('!merged!')) {
    try {
      const parts = text.split('!');
      const title = decodeURIComponent(parts[2] || '');
      let content = decodeURIComponent(parts[3] || '');

      let messagesArray: any[] = [];
      let attachmentUrl = msg.attachmentUrl;

      const mergedMessagesMatch = text.match(/\|\|\|MERGED_MESSAGES:(.+)$/);
      if (mergedMessagesMatch) {
        try {
          messagesArray = JSON.parse(decodeURIComponent(mergedMessagesMatch[1]));
          content = content.replace(/\|\|\|MERGED_MESSAGES:.+$/, '');
        } catch (_e) {
          console.error('Failed to parse mergedForwardMessages:', _e);
        }
      }

      const attachmentUrlMatch = text.match(/\|\|\|ATTACHMENT_URL:(.+)$/);
      if (attachmentUrlMatch && !attachmentUrl) {
        attachmentUrl = attachmentUrlMatch[1];
        content = content.replace(/\|\|\|ATTACHMENT_URL:.+$/, '');
      }

      if (messagesArray.length === 0 && msg.attachment) {
        try {
          const data = typeof msg.attachment === 'string' ? JSON.parse(msg.attachment) : msg.attachment;
          messagesArray = Array.isArray(data) ? data : Array.isArray(data?.messages) ? data.messages : [];
        } catch (_e) {
          // Ignore
        }
      }

      giftedm.type = 'mergedForward';
      giftedm.mergedForwardTitle = title;
      giftedm.mergedForwardContent = content;
      giftedm.mergedForwardAttachmentUrl = attachmentUrl;
      if (messagesArray.length > 0) {
        giftedm.mergedForwardMessages = messagesArray;
      }
      // 保持原始文本格式，以便收藏状态下可以从文本解析
      giftedm.text = `${title}: ${content}`;
      // 确保 message 属性包含完整的原始文本（包含 |||MERGED_MESSAGES: 标记）
      giftedm.message = text;
    } catch (_error) {
      giftedm.text = text.replace(/\\n/g, '\n');
    }
  } else if (msg.type === 5 || (typeof msg.type === 'string' && msg.type === 'mergedForward')) {
    try {
      const titleMatch = text.match(/^([^:：]+)[:：]\s*(.+)$/);
      const title = titleMatch ? titleMatch[1].trim() : msg.mergedForwardTitle || '聊天记录';
      const content = titleMatch ? titleMatch[2].trim() : msg.mergedForwardContent || text;

      let messagesArray: any[] = [];
      if (msg.attachment) {
        try {
          const data = typeof msg.attachment === 'string' ? JSON.parse(msg.attachment) : msg.attachment;
          messagesArray = Array.isArray(data) ? data : Array.isArray(data?.messages) ? data.messages : [];
        } catch (_e) {
          // Ignore
        }
      }

      giftedm.type = 'mergedForward';
      giftedm.mergedForwardTitle = title;
      giftedm.mergedForwardContent = content;
      giftedm.mergedForwardAttachmentUrl = msg.attachmentUrl;
      if (messagesArray.length > 0) {
        giftedm.mergedForwardMessages = messagesArray;
      }
      giftedm.text = `${title}: ${content}`;
    } catch (_error) {
      giftedm.text = text.replace(/\\n/g, '\n');
    }
  } else {
    giftedm.text = text.replace(/\\n/g, '\n');
  }

  return giftedm;
};

export const formatQuoteText = (quoteText: string): string => {
  if (!quoteText || typeof quoteText !== 'string') {
    return '';
  }

  const trimmedText = quoteText.trim();
  if (!trimmedText) {
    return '';
  }

  let plainText = trimmedText;
  if (typeof document !== 'undefined') {
    const tempDiv = document.createElement('div');
    tempDiv.textContent = plainText;
    plainText = tempDiv.textContent || plainText;
  } else {
    plainText = plainText.replace(/<[^>]*>/g, '');
  }

  plainText = plainText.trim().replace(/\s+/g, ' ');

  return plainText;
};

export const parseMessagesToGiftedFormat = (
  messages: IRawMessage[],
  options: IParseMessageOptions = {}
): IGiftedMessage[] => {
  if (!messages || !Array.isArray(messages)) {
    return [];
  }

  return messages
    .map((msg: IRawMessage, index: number) => parseMessageToGiftedFormat(msg, index, options))
    .filter((msg): msg is IGiftedMessage => msg !== null);
};
